import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { FieldSelect } from "@/components/ui/field-select";
import { EstablishmentDialog } from "@/components/layout/EstablishmentSwitcher";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Loader2, UserPlus, Mail, RefreshCw, Ban, CheckCircle2, Building2, Plus, Pencil, Power, Bell, Globe } from "lucide-react";
import SubscriptionBilling from "@/components/SubscriptionBilling";

const ROLE_LABELS = { owner: "Propriétaire", manager: "Manager", reception: "Réception", guide: "Guide", accountant: "Comptable" };
const ROLE_OPTS = Object.entries(ROLE_LABELS).map(([v, l]) => ({ value: v, label: l }));

export default function Utilisateurs() {
  const { orgId, role: myRole } = useAuth();
  const qc = useQueryClient();
  const [inviteOpen, setInviteOpen] = useState(false);
  const isOwner = myRole === "owner";
  const canInvite = myRole === "owner" || myRole === "manager";

  const { data: usersData, isLoading } = useQuery({
    queryKey: ["org-users", orgId],
    queryFn: async () => (await api.get("/api/users", { params: { organization_id: orgId } })).data,
    enabled: !!orgId && canInvite, retry: false,
  });
  const { data: invData } = useQuery({
    queryKey: ["org-invites", orgId],
    queryFn: async () => (await api.get("/api/invitations", { params: { organization_id: orgId } })).data,
    enabled: !!orgId && canInvite, retry: false,
  });
  const users = usersData?.users || [];
  const invites = invData?.invitations || [];

  const patch = useMutation({
    mutationFn: async ({ id, body }) => (await api.patch(`/api/users/${id}`, body, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Membre mis à jour"); qc.invalidateQueries({ queryKey: ["org-users", orgId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  const inviteAction = useMutation({
    mutationFn: async ({ id, action }) => (await api.post(`/api/invitations/${id}/${action}`, {}, { params: { organization_id: orgId } })).data,
    onSuccess: (_d, v) => { toast.success(v.action === "resend" ? "Invitation renvoyée" : "Invitation annulée"); qc.invalidateQueries({ queryKey: ["org-invites", orgId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });

  if (!canInvite) return (
    <div className="mx-auto max-w-[1100px] p-8" data-testid="utilisateurs-denied">
      <div className="rounded-2xl border border-slate-200 bg-white py-16 text-center"><p className="text-slate-700">Vous n'avez pas accès à la gestion des utilisateurs.</p></div>
    </div>
  );


  return (
    <div className="mx-auto max-w-[1100px] space-y-6 p-4 sm:p-6 lg:p-8" data-testid="utilisateurs-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div><h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">Paramètres</h1>
          <p className="text-sm text-slate-500">Organisation, établissements, utilisateurs et notifications.</p>
          <div className="mt-2"><PlanBadge /></div></div>
        {canInvite && <button onClick={() => setInviteOpen(true)} data-testid="invite-user-button" className="flex items-center gap-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-white hover:bg-blue-700"><UserPlus className="h-4 w-4" /> Inviter</button>}
      </div>

      {isOwner && <SubscriptionBilling orgId={orgId} />}

      {isLoading ? <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div> : (
        <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="users-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-400"><tr>{["Nom", "E-mail", "Rôle", "Statut", "Actions"].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody className="divide-y divide-slate-100">
              {users.map((u) => (
                <tr key={u.member_id} data-testid={`user-row-${u.member_id}`}>
                  <td className="px-4 py-3 font-medium text-slate-800">{u.name || "—"}</td>
                  <td className="px-4 py-3 text-slate-500">{u.email}</td>
                  <td className="px-4 py-3">
                    {isOwner ? <FieldSelect testId={`role-select-${u.member_id}`} className="w-40" value={u.role} onChange={(v) => patch.mutate({ id: u.member_id, body: { role: v } })} options={ROLE_OPTS} />
                      : <span className="text-slate-600">{ROLE_LABELS[u.role] || u.role}</span>}
                  </td>
                  <td className="px-4 py-3">{u.active ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Actif</span> : <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">Désactivé</span>}</td>
                  <td className="px-4 py-3">
                    {isOwner && (u.active
                      ? <button onClick={() => patch.mutate({ id: u.member_id, body: { active: false } })} data-testid={`deactivate-${u.member_id}`} className="flex items-center gap-1 text-xs font-medium text-rose-600 hover:underline"><Ban className="h-3.5 w-3.5" /> Désactiver</button>
                      : <button onClick={() => patch.mutate({ id: u.member_id, body: { active: true } })} data-testid={`reactivate-${u.member_id}`} className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:underline"><CheckCircle2 className="h-3.5 w-3.5" /> Réactiver</button>)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {canInvite && invites.length > 0 && (
        <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="pending-invites">
          <h3 className="mb-2 text-sm font-semibold text-slate-700">Invitations en attente</h3>
          <div className="space-y-1">
            {invites.map((i) => (
              <div key={i.id} className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-100 px-3 py-2 text-sm" data-testid={`invite-${i.id}`}>
                <span className="text-slate-700"><Mail className="mr-1.5 inline h-3.5 w-3.5 text-slate-400" />{i.email} · {ROLE_LABELS[i.role] || i.role}</span>
                <div className="flex gap-3">
                  <button onClick={() => inviteAction.mutate({ id: i.id, action: "resend" })} data-testid={`resend-${i.id}`} className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"><RefreshCw className="h-3.5 w-3.5" /> Renvoyer</button>
                  <button onClick={() => inviteAction.mutate({ id: i.id, action: "revoke" })} data-testid={`revoke-${i.id}`} className="flex items-center gap-1 text-xs font-medium text-rose-600 hover:underline"><Ban className="h-3.5 w-3.5" /> Annuler</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <InviteDialog open={inviteOpen} onOpenChange={setInviteOpen} orgId={orgId} isOwner={isOwner} onDone={() => qc.invalidateQueries({ queryKey: ["org-invites", orgId] })} />

      {isOwner && <EstablishmentsSection orgId={orgId} />}
      {isOwner && <NotificationsSection orgId={orgId} />}
      {isOwner && <OrgPrefsSection orgId={orgId} />}
    </div>
  );
}

function EstablishmentsSection({ orgId }) {
  const qc = useQueryClient();
  const [dialog, setDialog] = useState({ open: false, editing: null });
  const { data } = useQuery({
    queryKey: ["locations", orgId],
    queryFn: async () => (await api.get("/api/locations", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });
  const locs = data?.locations || [];
  const toggle = useMutation({
    mutationFn: async ({ id, active }) => (await api.patch(`/api/locations/${id}`, { active }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Établissement mis à jour"); qc.invalidateQueries({ queryKey: ["locations", orgId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="establishments-section">
      <div className="flex items-center justify-between border-b border-slate-100 p-4">
        <div className="flex items-center gap-2"><Building2 className="h-4 w-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-800">Établissements</h3></div>
        <button onClick={() => setDialog({ open: true, editing: null })} data-testid="settings-add-establishment" className="flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-semibold text-white hover:bg-blue-700"><Plus className="h-4 w-4" /> Ajouter</button>
      </div>
      <table className="w-full text-sm">
        <tbody className="divide-y divide-slate-100">
          {locs.map((l) => (
            <tr key={l.id} data-testid={`establishment-settings-${l.id}`}>
              <td className="px-4 py-3">
                <p className="font-medium text-slate-800">{l.name}{l.is_primary && <span className="ml-2 rounded-full bg-blue-50 px-2 py-0.5 text-xs font-medium text-primary">Principal</span>}</p>
                {l.city && <p className="text-xs text-slate-400">{l.city}{l.country ? `, ${l.country}` : ""}</p>}
              </td>
              <td className="px-4 py-3">{l.active !== false ? <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-0.5 text-xs font-medium text-emerald-700">Actif</span> : <span className="rounded-full border border-slate-300 bg-slate-100 px-2.5 py-0.5 text-xs font-medium text-slate-500">Désactivé</span>}</td>
              <td className="px-4 py-3 text-right">
                <div className="flex justify-end gap-3">
                  <button onClick={() => setDialog({ open: true, editing: l })} data-testid={`establishment-edit-${l.id}`} className="flex items-center gap-1 text-xs font-medium text-primary hover:underline"><Pencil className="h-3.5 w-3.5" /> Modifier</button>
                  {!l.is_primary && <button onClick={() => toggle.mutate({ id: l.id, active: l.active === false })} data-testid={`establishment-toggle-${l.id}`} className={`flex items-center gap-1 text-xs font-medium hover:underline ${l.active === false ? "text-emerald-600" : "text-rose-600"}`}><Power className="h-3.5 w-3.5" /> {l.active === false ? "Réactiver" : "Désactiver"}</button>}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <EstablishmentDialog open={dialog.open} editing={dialog.editing} onClose={() => setDialog({ open: false, editing: null })} onCreated={() => qc.invalidateQueries({ queryKey: ["locations", orgId] })} />
    </div>
  );
}

function InviteDialog({ open, onOpenChange, orgId, isOwner, onDone }) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("reception");
  const opts = isOwner ? ROLE_OPTS : ROLE_OPTS.filter((o) => o.value !== "owner");
  const mut = useMutation({
    mutationFn: async () => (await api.post("/api/invitations", { organization_id: orgId, email, role, location_ids: [] })).data,
    onSuccess: () => { toast.success("Invitation envoyée"); onDone(); onOpenChange(false); setEmail(""); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="invite-dialog">
        <DialogHeader><DialogTitle>Inviter un collaborateur</DialogTitle>
          <DialogDescription className="sr-only">Envoyer une invitation par e-mail.</DialogDescription></DialogHeader>
        <div className="space-y-3">
          <div><label className="mb-1 block text-sm font-medium text-slate-700">E-mail</label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="invite-email-input" className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20" placeholder="collaborateur@exemple.com" /></div>
          <div><label className="mb-1 block text-sm font-medium text-slate-700">Rôle</label>
            <FieldSelect testId="invite-role-select" className="w-full" value={role} onChange={setRole} options={opts} /></div>
          <button onClick={() => mut.mutate()} disabled={mut.isPending || !email.trim()} data-testid="send-invite-button" className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">
            {mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Envoyer l'invitation
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}


function PlanBadge() {
  const { plan, plans } = useAuth();
  if (!plan) return null;
  const name = plans?.[plan]?.name || plan;
  return <span data-testid="current-plan-badge" className="inline-flex items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">Offre actuelle : {name}</span>;
}

const NOTIF_ITEMS = [
  ["unpaid_completed", "Réservation terminée mais impayée"],
  ["maintenance_due", "Maintenance arrivant à échéance"],
  ["critical_incident", "Incident critique"],
  ["team_event", "Événement important concernant l'équipe"],
  ["daily_summary", "Résumé quotidien"],
  ["weekly_summary", "Résumé hebdomadaire (dimanche)"],
];

function Switch({ on, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid} aria-checked={on} role="switch"
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${on ? "bg-primary" : "bg-slate-300"}`}>
      <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${on ? "translate-x-5" : "translate-x-0.5"}`} />
    </button>
  );
}

function NotificationsSection({ orgId }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["notif-prefs", orgId],
    queryFn: async () => (await api.get("/api/settings/notifications", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });
  const prefs = data?.prefs || {};
  const mut = useMutation({
    mutationFn: async (next) => (await api.patch("/api/settings/notifications", { prefs: next }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Préférences enregistrées"); qc.invalidateQueries({ queryKey: ["notif-prefs", orgId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="notifications-section">
      <div className="flex items-center gap-2 border-b border-slate-100 p-4"><Bell className="h-4 w-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-800">Notifications email</h3></div>
      <div className="divide-y divide-slate-100">
        {NOTIF_ITEMS.map(([k, lbl]) => (
          <div key={k} className="flex items-center justify-between px-4 py-3" data-testid={`notif-row-${k}`}>
            <span className="text-sm text-slate-700">{lbl}</span>
            <Switch on={!!prefs[k]} onClick={() => mut.mutate({ ...prefs, [k]: !prefs[k] })} testid={`notif-toggle-${k}`} />
          </div>
        ))}
      </div>
      <p className="px-4 py-3 text-xs text-slate-400">ActiPilote n'envoie jamais d'e-mail à chaque réservation. Les résumés respectent le fuseau horaire de votre organisation.</p>
    </div>
  );
}

const CURRENCIES = ["MAD", "EUR", "USD", "GBP", "AED", "TND", "XOF"];
const LANGS = [{ value: "fr", label: "Français" }, { value: "en", label: "English" }, { value: "ar", label: "العربية" }];
const TIMEZONES = ["Africa/Casablanca", "Europe/Paris", "Europe/London", "Europe/Madrid", "Africa/Tunis", "Africa/Dakar", "UTC"];
const sInput = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20";

function OrgPrefsSection({ orgId }) {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["org-info", orgId],
    queryFn: async () => (await api.get("/api/organization", { params: { organization_id: orgId } })).data,
    enabled: !!orgId,
  });
  const [f, setF] = useState(null);
  React.useEffect(() => { if (data) setF(data); }, [data]);
  const mut = useMutation({
    mutationFn: async () => (await api.patch("/api/organization", {
      name: f.name, legal_name: f.legal_name, country: f.country, city: f.city, phone: f.phone,
      email: f.email, default_currency: f.default_currency, default_language: f.default_language,
      timezone: f.timezone,
    }, { params: { organization_id: orgId } })).data,
    onSuccess: () => { toast.success("Organisation mise à jour"); qc.invalidateQueries({ queryKey: ["org-info", orgId] }); },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail) || e.message),
  });
  if (!f) return null;
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  return (
    <div className="rounded-2xl border border-slate-200 bg-white" data-testid="org-prefs-section">
      <div className="flex items-center gap-2 border-b border-slate-100 p-4"><Globe className="h-4 w-4 text-slate-400" /><h3 className="text-sm font-semibold text-slate-800">Préférences de l'organisation</h3></div>
      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Nom de l'organisation</label><input value={f.name || ""} onChange={set("name")} data-testid="org-name-input" className={sInput} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Raison sociale</label><input value={f.legal_name || ""} onChange={set("legal_name")} data-testid="org-legal-input" className={sInput} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Pays</label><input value={f.country || ""} onChange={set("country")} data-testid="org-country-input" className={sInput} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Ville</label><input value={f.city || ""} onChange={set("city")} data-testid="org-city-input" className={sInput} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Téléphone</label><input value={f.phone || ""} onChange={set("phone")} className={sInput} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">E-mail</label><input value={f.email || ""} onChange={set("email")} className={sInput} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Devise</label><FieldSelect testId="org-currency-select" className="w-full" value={f.default_currency || "MAD"} onChange={(v) => setF({ ...f, default_currency: v })} options={CURRENCIES.map((c) => ({ value: c, label: c }))} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Langue</label><FieldSelect testId="org-language-select" className="w-full" value={f.default_language || "fr"} onChange={(v) => setF({ ...f, default_language: v })} options={LANGS} /></div>
        <div><label className="mb-1 block text-xs font-medium text-slate-500">Fuseau horaire</label><FieldSelect testId="org-timezone-select" className="w-full" value={f.timezone || "Africa/Casablanca"} onChange={(v) => setF({ ...f, timezone: v })} options={TIMEZONES.map((t) => ({ value: t, label: t }))} /></div>
      </div>
      <div className="border-t border-slate-100 p-4">
        <button onClick={() => mut.mutate()} disabled={mut.isPending || !f.name?.trim()} data-testid="save-org-prefs-button" className="flex items-center justify-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white hover:bg-blue-700 disabled:opacity-50">{mut.isPending && <Loader2 className="h-4 w-4 animate-spin" />} Enregistrer</button>
      </div>
    </div>
  );
}
